import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Form_Template_Field } from 'src/app/Interfaces/Forms/FormTemplate.interface';
import Swal from 'sweetalert2';


@Component({
  selector: 'app-form-fill-cdms',
  templateUrl: './form-fill-cdms.component.html',
  styleUrls: ['./form-fill-cdms.component.scss', '../../../Styles/GlobalStyles.css'],
  standalone: false
})
export class FormFillCDMSComponent implements OnInit, OnChanges {

  @Input() fields: Form_Template_Field[] = [];
  @Input() mode: 'fill' | 'preview' | 'readonly' = 'fill';
  @Input() values: { [fieldId: number]: string | null } = {};

  @Output() valuesChange = new EventEmitter<{ ID_Field: number, Value: string | null }[]>();
  @Output() filesChange = new EventEmitter<{ ID_Field: number, File: File }[]>();

  public formGroup: FormGroup = new FormGroup({});

  // File/image state per field
  public fileMap: { [key: string]: File } = {};
  public previewMap: { [key: string]: string } = {};

  // Checkbox state per field
  public checkboxMap: { [key: string]: boolean } = {};

  // Table state per field
  public tableDataMap: { [key: string]: any[] } = {};
  public tableColsCache: { [key: string]: { name: string, type: string }[] } = {};
  private tableSyncTimer: any = null;

  constructor() {}

  ngOnInit(): void {
    try {
      this.buildForm();
    } catch (e) {
      console.error('[FormFill] buildForm error:', e);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] || changes['values']) {
      try {
        this.buildForm();
      } catch (e) {
        console.error('[FormFill] buildForm onChange error:', e);
      }
    }
  }

  private buildForm(): void {
    console.log('[FormFill] buildForm called, fields:', this.fields?.length, 'mode:', this.mode);
    const group: any = {};

    for (const field of this.fields) {
      if (field.Field_Type === 'section') continue;

      const fieldId = field.ID || field.Field_Order;
      const validators: any[] = [];

      if (field.Required) {
        validators.push(Validators.required);
      }

      if (field.Validations) {
        try {
          const validations = typeof field.Validations === 'string'
            ? JSON.parse(field.Validations) : field.Validations;
          if (validations.minLength) validators.push(Validators.minLength(validations.minLength));
          if (validations.maxLength) validators.push(Validators.maxLength(validations.maxLength));
          if (validations.pattern) validators.push(Validators.pattern(validations.pattern));
          if (validations.min !== undefined) validators.push(Validators.min(validations.min));
          if (validations.max !== undefined) validators.push(Validators.max(validations.max));
        } catch (e) {}
      }

      // Agregar validadores específicos por tipo
      if (field.Field_Type === 'email') {
        validators.push(Validators.email);
      } else if (field.Field_Type === 'url') {
        // URL básica - checks for http/https
        validators.push((c: FormControl) => {
          if (!c.value) return null;
          try {
            new URL(c.value);
            return null;
          } catch (e) {
            return { 'invalidUrl': true };
          }
        });
      }

      // Initialize table data
      if (field.Field_Type === 'table') {
        console.log('[FormFill] Table field found:', field.Label, 'Options:', field.Options);
        const fieldKey = this.getFieldKey(field);
        const existingVal = this.values[fieldId];
        console.log('[FormFill] Table existingVal:', existingVal);
        if (existingVal) {
          try {
            this.tableDataMap[fieldKey] = JSON.parse(existingVal);
          } catch (e) {
            this.tableDataMap[fieldKey] = [this.createEmptyTableRow(field)];
          }
        } else if (!this.tableDataMap[fieldKey] || this.tableDataMap[fieldKey].length === 0) {
          this.tableDataMap[fieldKey] = [this.createEmptyTableRow(field)];
        }
      }

      // Set default values per field type
      let defaultValue: any = this.values[fieldId] ?? field.Default_Value ?? '';

      if (field.Field_Type === 'boolean') {
        defaultValue = defaultValue === 'true' || defaultValue === true || String(defaultValue) === 'true' || false;

      } else if (field.Field_Type === 'number') {
        // p-inputNumber needs a number, not a string
        if (defaultValue !== '' && defaultValue !== null) {
          const num = Number(defaultValue);
          defaultValue = isNaN(num) ? null : num;
        } else {
          defaultValue = null;
        }

      } else if (field.Field_Type === 'date' && defaultValue) {
        // p-datePicker needs a Date object
        try {
          const d = new Date(defaultValue);
          if (!isNaN(d.getTime())) defaultValue = d;
        } catch (e) {}

      } else if (field.Field_Type === 'multi_select' && defaultValue) {
        // p-multiSelect needs an array
        if (typeof defaultValue === 'string') {
          try {
            const parsed = JSON.parse(defaultValue);
            if (Array.isArray(parsed)) defaultValue = parsed;
          } catch (e) {
            // Try comma-separated
            defaultValue = defaultValue.split(',').map((s: string) => s.trim()).filter((s: string) => s);
          }
        }

      } else if (field.Field_Type === 'table') {
        const fieldKey = this.getFieldKey(field);
        defaultValue = this.tableDataMap[fieldKey] ? JSON.stringify(this.tableDataMap[fieldKey]) : '[]';
      } else if (field.Field_Type === 'checkbox_list' && defaultValue) {
        // Para checkbox_list, intenta parsear como JSON array
        if (typeof defaultValue === 'string') {
          try {
            const parsed = JSON.parse(defaultValue);
            if (Array.isArray(parsed)) {
              // Inicializar checkboxMap con los valores
              const fieldKey = this.getFieldKey(field);
              for (const opt of this.getOptions(field)) {
                this.checkboxMap[`${fieldKey}_${opt}`] = parsed.includes(opt);
              }
            }
          } catch (e) {
            defaultValue = '';
          }
        }
      }

      const fieldKey = this.getFieldKey(field);
      if ((field.Field_Type === 'image' || field.Field_Type === 'file') && typeof defaultValue === 'string' && defaultValue.startsWith('data:image')) {
        this.previewMap[fieldKey] = defaultValue;
      }

      group[fieldKey] = new FormControl(
        { value: defaultValue, disabled: this.mode === 'readonly' },
        validators
      );
    }

    this.formGroup = new FormGroup(group);
    console.log('[FormFill] FormGroup created with controls:', Object.keys(group));
    console.log('[FormFill] tableDataMap:', JSON.stringify(this.tableDataMap));

    this.formGroup.valueChanges.subscribe(() => {
      this.emitValues();
    });

    // Emit initial values so parent has them even if no changes are made
    this.emitValues();


    // Load signature images onto canvases after render
    setTimeout(() => {
      for (const field of this.fields) {
        if (field.Field_Type === 'signature') {
          const fieldId = field.ID || field.Field_Order;
          const val = this.values[fieldId];
          if (val && val.startsWith('data:image')) {
            this.loadSignatureImage(field, val);
          }
        }
      }
    }, 200);
  }

  public getOptions(field: Form_Template_Field): string[] {
    if (!field.Options) return [];
    try {
      const opts = typeof field.Options === 'string' ? JSON.parse(field.Options) : field.Options;
      return Array.isArray(opts) ? opts : [];
    } catch (e) {
      return [];
    }
  }

  public getFieldControl(field: Form_Template_Field): FormControl {
    return this.formGroup.get(this.getFieldKey(field)) as FormControl;
  }

  public isInvalid(field: Form_Template_Field): boolean {
    const control = this.getFieldControl(field);
    return control && control.invalid && (control.dirty || control.touched);
  }

  public getErrorMessage(field: Form_Template_Field): string {
    const control = this.getFieldControl(field);
    if (!control || !control.errors) return '';
    if (control.errors['required']) return `${field.Label} es obligatorio`;
    if (control.errors['email']) return `${field.Label} debe ser un email válido`;
    if (control.errors['invalidUrl']) return `${field.Label} debe ser una URL válida`;
    if (control.errors['minlength']) return `Mínimo ${control.errors['minlength'].requiredLength} caracteres`;
    if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
    if (control.errors['pattern']) return `Formato no válido`;
    if (control.errors['min']) return `Valor mínimo: ${control.errors['min'].min}`;
    if (control.errors['max']) return `Valor máximo: ${control.errors['max'].max}`;
    return 'Campo no válido';
  }

  // ======================== FILE / IMAGE HANDLING ========================

  public onFileSelect(event: Event, field: Form_Template_Field): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const fieldKey = this.getFieldKey(field);
    
    console.log(`[FormFill] Selecting file for field ${field.Label} (Type: ${field.Field_Type}):`, file.name, file.type);

    // Validation for image fields
    if (field.Field_Type === 'image') {
      const isImg = file.type.startsWith('image/') || 
                    /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
      
      if (!isImg) {
        Swal.fire({
          title: 'Archivo no válido',
          text: 'Este campo solo acepta imágenes (PNG, JPG, WEBP, GIF).',
          icon: 'error',
          confirmButtonColor: '#20a8e1'
        });
        input.value = '';
        this.removeFile(field);
        return;
      }
    }

    this.fileMap[fieldKey] = file;


    // Set filename in the FormControl for validation
    const control = this.getFieldControl(field);
    if (control) {
      control.setValue(file.name);
      control.markAsDirty();
    }

    // Generate preview if it's an image
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewMap[fieldKey] = e.target.result;
        this.emitValues(); // Emits after base64 is fully loaded so it reaches the PDF Generator payload
      };
      reader.readAsDataURL(file);
    } else {
      this.previewMap[fieldKey] = '';
    }

    this.emitFiles();
  }

  public removeFile(field: Form_Template_Field): void {
    const fieldKey = this.getFieldKey(field);
    delete this.fileMap[fieldKey];
    delete this.previewMap[fieldKey];

    const control = this.getFieldControl(field);
    if (control) {
      control.setValue('');
      control.markAsDirty();
    }

    this.emitFiles();
    this.emitValues();
  }

  public hasFile(field: Form_Template_Field): boolean {
    const fieldKey = this.getFieldKey(field);
    return !!this.fileMap[fieldKey] || (field.Field_Type === 'image' && !!this.previewMap[fieldKey]);
  }

  public getFileName(field: Form_Template_Field): string {
    const fieldKey = this.getFieldKey(field);
    const file = this.fileMap[fieldKey];
    if (file) return file.name;
    if (field.Field_Type === 'image' && this.previewMap[fieldKey]) return 'imagen_adjunta.png';
    return '';
  }

  public getPreview(field: Form_Template_Field): string {
    return this.previewMap[this.getFieldKey(field)] || '';
  }

  public isImageField(field: Form_Template_Field): boolean {
    return field.Field_Type === 'image';
  }

  public getAcceptTypes(field: Form_Template_Field): string {
    if (field.Field_Type === 'image') {
      return 'image/*,.png,.jpg,.jpeg,.gif,.webp';
    }

    return ''; // all types for generic file
  }

  public getFieldKey(field: Form_Template_Field): string {
    return `field_${field.UI_ID || field.ID || field.Field_Order}`;
  }

  private emitFiles(): void {
    const files: { ID_Field: number, File: File }[] = [];
    for (const field of this.fields) {
      if (field.Field_Type !== 'file' && field.Field_Type !== 'image') continue;
      const fieldKey = this.getFieldKey(field);
      if (this.fileMap[fieldKey]) {
        files.push({
          ID_Field: (field.ID || field.Field_Order)!,
          File: this.fileMap[fieldKey]
        });
      }
    }
    this.filesChange.emit(files);
  }

  private emitValues(): void {
    const values: { ID_Field: number, Value: string | null }[] = [];
    for (const field of this.fields) {
      if (field.Field_Type === 'section') continue;
      const fieldId = field.ID || field.Field_Order;
      const control = this.formGroup.get(this.getFieldKey(field));
      let val: string | null = null;

      if (control && control.value !== null && control.value !== undefined) {
        const raw = control.value;

        if (field.Field_Type === 'date' && raw instanceof Date) {
          const year = raw.getFullYear();
          const month = String(raw.getMonth() + 1).padStart(2, '0');
          const day = String(raw.getDate()).padStart(2, '0');
          val = `${year}-${month}-${day}`;
        } else if (field.Field_Type === 'multi_select' && Array.isArray(raw)) {

          val = JSON.stringify(raw);
        } else if ((field.Field_Type === 'image' || field.Field_Type === 'file') && this.previewMap[this.getFieldKey(field)]) {
          val = this.previewMap[this.getFieldKey(field)];
        } else {
          val = String(raw);
        }
      }

      values.push({ ID_Field: fieldId!, Value: val });
    }
    this.valuesChange.emit(values);
  }

  // ======================== SIGNATURE HANDLING ========================

  private sigDrawing: { [key: string]: boolean } = {};

  private getSigCanvas(field: Form_Template_Field): HTMLCanvasElement | null {
    const id = `sigCanvas_${field.UI_ID || field.ID || field.Field_Order}`;
    return document.getElementById(id) as HTMLCanvasElement | null;
  }

  private getSigCtx(field: Form_Template_Field): CanvasRenderingContext2D | null {
    const canvas = this.getSigCanvas(field);
    return canvas ? canvas.getContext('2d') : null;
  }

  public onSigStart(event: MouseEvent, field: Form_Template_Field): void {
    const key = this.getFieldKey(field);
    this.sigDrawing[key] = true;
    const ctx = this.getSigCtx(field);
    if (!ctx) return;
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(event.clientX - rect.left, event.clientY - rect.top);
  }

  public onSigMove(event: MouseEvent, field: Form_Template_Field): void {
    const key = this.getFieldKey(field);
    if (!this.sigDrawing[key]) return;
    const ctx = this.getSigCtx(field);
    if (!ctx) return;
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#333';
    ctx.lineTo(event.clientX - rect.left, event.clientY - rect.top);
    ctx.stroke();
  }

  public onSigTouchStart(event: TouchEvent, field: Form_Template_Field): void {
    event.preventDefault();
    const key = this.getFieldKey(field);
    this.sigDrawing[key] = true;
    const ctx = this.getSigCtx(field);
    if (!ctx) return;
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    const touch = event.touches[0];
    ctx.beginPath();
    ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
  }

  public onSigTouchMove(event: TouchEvent, field: Form_Template_Field): void {
    event.preventDefault();
    const key = this.getFieldKey(field);
    if (!this.sigDrawing[key]) return;
    const ctx = this.getSigCtx(field);
    if (!ctx) return;
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    const touch = event.touches[0];
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#333';
    ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
    ctx.stroke();
  }

  public onSigEnd(field: Form_Template_Field): void {
    const key = this.getFieldKey(field);
    this.sigDrawing[key] = false;
    // Save as base64 to the form control
    const canvas = this.getSigCanvas(field);
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const control = this.getFieldControl(field);
    if (control) {
      control.setValue(dataUrl);
      control.markAsDirty();
    }
  }

  private loadSignatureImage(field: Form_Template_Field, dataUrl: string): void {
    const canvas = this.getSigCanvas(field);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = dataUrl;
  }

  public clearSignature(field: Form_Template_Field): void {
    const canvas = this.getSigCanvas(field);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    const control = this.getFieldControl(field);
    if (control) {
      control.setValue('');
      control.markAsDirty();
    }
  }

  // ======================== TABLE HANDLING ========================

  public getTableColumns(field: Form_Template_Field): { name: string, type: string }[] {
    if (!field.Options) return [];
    const cacheKey = typeof field.Options === 'string' ? field.Options : JSON.stringify(field.Options);
    
    if (this.tableColsCache[cacheKey]) {
      return this.tableColsCache[cacheKey];
    }
    try {
      const parsed = typeof field.Options === 'string' ? JSON.parse(field.Options) : field.Options;
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name) {
        this.tableColsCache[cacheKey] = parsed;
        return parsed;
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  public getTableRows(field: Form_Template_Field): any[] {
    const fieldKey = this.getFieldKey(field);
    return this.tableDataMap[fieldKey] || [];
  }

  private createEmptyTableRow(field: Form_Template_Field): any {
    const cols = this.getTableColumns(field);
    const row: any = {};
    for (const col of cols) {
      row[col.name] = col.type === 'boolean' ? false : '';
    }
    return row;
  }

  public addTableRow(field: Form_Template_Field): void {
    const fieldKey = this.getFieldKey(field);
    if (!this.tableDataMap[fieldKey]) {
      this.tableDataMap[fieldKey] = [];
    }
    this.tableDataMap[fieldKey] = [...this.tableDataMap[fieldKey], this.createEmptyTableRow(field)];
    this.syncTableToForm(field);
  }

  public removeTableRow(field: Form_Template_Field, index: number): void {
    const fieldKey = this.getFieldKey(field);
    if (this.tableDataMap[fieldKey] && this.tableDataMap[fieldKey].length > 1) {
      this.tableDataMap[fieldKey].splice(index, 1);
      this.tableDataMap[fieldKey] = [...this.tableDataMap[fieldKey]];
      this.syncTableToForm(field);
    }
  }

  public setCellValue(field: Form_Template_Field, rowIndex: number, colName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const fieldKey = this.getFieldKey(field);
    if (this.tableDataMap[fieldKey]?.[rowIndex]) {
      this.tableDataMap[fieldKey][rowIndex][colName] = input.value;
      this.syncTableToForm(field);
    }
  }

  public setCellChecked(field: Form_Template_Field, rowIndex: number, colName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const fieldKey = this.getFieldKey(field);
    if (this.tableDataMap[fieldKey]?.[rowIndex]) {
      this.tableDataMap[fieldKey][rowIndex][colName] = input.checked;
      this.syncTableToForm(field);
    }
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private syncTableToForm(field: Form_Template_Field): void {
    const control = this.formGroup.get(this.getFieldKey(field));
    if (control) {
      const fieldKey = this.getFieldKey(field);
      control.setValue(JSON.stringify(this.tableDataMap[fieldKey]), { emitEvent: false });
    }
    this.emitValues();
  }

  public isValid(): boolean {
    this.formGroup.markAllAsTouched();
    return this.formGroup.valid;
  }

  // ======================== CHECKBOX LIST HANDLING ========================

  public onCheckboxChange(field: Form_Template_Field, option: string, event: any): void {
    const control = this.getFieldControl(field);
    const fieldKey = this.getFieldKey(field);
    
    // Recolectar todos los checkboxes seleccionados
    const selectedOptions: string[] = [];
    for (const opt of this.getOptions(field)) {
      const key = `${fieldKey}_${opt}`;
      if (this.checkboxMap[key]) {
        selectedOptions.push(opt);
      }
    }
    
    // Guardar como JSON array en el FormControl
    control.setValue(JSON.stringify(selectedOptions));
    control.markAsDirty();
    this.emitValues();
  }

  // ======================== QR CODE / BARCODE HANDLING ========================

  public scanQRCode(field: Form_Template_Field): void {
    // Placeholder - implementar con cualquier librería de QR (ZXing, QuaggaJS, etc)
    // Por ahora, simular un prompt
    const result = prompt('Escanear código QR (simular entrada):');
    if (result) {
      const control = this.getFieldControl(field);
      control.setValue(result);
      control.markAsDirty();
      this.emitValues();
    }
  }

  public scanBarcode(field: Form_Template_Field): void {
    // Placeholder - implementar con librería de barcode (QuaggaJS, etc)
    // Por ahora, simular un prompt
    const result = prompt('Escanear código de barras (simular entrada):');
    if (result) {
      const control = this.getFieldControl(field);
      control.setValue(result);
      control.markAsDirty();
      this.emitValues();
    }
  }
}
